import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Navbar from './Navbar';
import VaultoLogo from './VaultoLogo';
import Footer from './Footer';
import { authAPI } from '../services/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token.');
        setVerifying(false);
        return;
      }

      try {
        const response = await authAPI.verifyResetToken({ token });
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset token. Please request a new one.');
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      await authAPI.resetPassword({
        token,
        new_password: data.password
      });
      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (verifying) {
      return (
        <div className="text-center py-8">
          <svg
            className="animate-spin h-12 w-12 text-pink-400 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-300">Verifying reset link...</p>
        </div>
      );
    }

    if (!tokenValid) {
      return (
        <div className="space-y-6">
          <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/50 text-red-200 px-4 py-4 rounded-xl flex items-start space-x-3">
            <svg className="h-6 w-6 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Invalid Reset Link</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>

          <div className="text-center space-y-4">
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center py-3 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              Request New Reset Link
            </Link>
            
            <div>
              <Link
                to="/login"
                className="text-sm font-semibold text-pink-400 hover:text-pink-300 transition-colors"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      );
    }

    if (isSuccess) {
      return (
        <div className="space-y-6">
          <div className="bg-green-500/20 backdrop-blur-sm border border-green-400/50 text-green-200 px-4 py-4 rounded-xl flex items-start space-x-3">
            <svg className="h-6 w-6 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold">Password Reset Successful!</p>
              <p className="text-sm mt-1">You can now log in with your new password. Redirecting to login...</p>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center py-3 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    return (
      <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {userEmail && (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 text-center">
            <p className="text-sm text-gray-300">
              Resetting password for: <span className="font-semibold text-white">{userEmail}</span>
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoFocus
              className={`appearance-none rounded-xl relative block w-full px-4 py-3 pr-12 border-0 bg-white/10 backdrop-blur-sm ${
                errors.password ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
              } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
              placeholder="Enter new password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              )}
            </button>
            {errors.password && (
              <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
            )}
          </div>

          <div className="relative">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`appearance-none rounded-xl relative block w-full px-4 py-3 pr-12 border-0 bg-white/10 backdrop-blur-sm ${
                errors.confirmPassword ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
              } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
              placeholder="Confirm new password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
              )}
            </button>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-400">{errors.confirmPassword.message}</p>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/50 text-red-200 px-4 py-3 rounded-xl flex items-start space-x-3">
            <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
          >
            {loading ? (
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : null}
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </div>

        <div className="text-center pt-4 border-t border-white/10">
          <p className="text-sm text-gray-300">
            Remember your password?{' '}
            <Link
              to="/login"
              className="font-semibold text-pink-400 hover:text-pink-300 transition-colors"
            >
              Back to login
            </Link>
          </p>
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <Navbar />
      <div className="relative flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Glassmorphism container */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center">
              {/* Logo */}
              <div className="mx-auto mb-6">
                <VaultoLogo className="h-16 w-auto mx-auto" showText={true} />
              </div>
              
              <h2 className="text-3xl font-extrabold text-white mb-2">
                Reset Password
              </h2>
              <p className="text-gray-300 text-sm">
                {verifying ? "Verifying your reset link..." : "Enter your new password below"}
              </p>
            </div>
            
            {renderContent()}
          </div>
        </div>
      </div>
      
      <Footer variant="dark" />
    </div>
  );
};

export default ResetPassword;

